import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FloppyDisk, Plus, Trash, PencilSimple, X, CircleNotch } from "@phosphor-icons/react";
import {
  fetchSettings, saveSettings,
  fetchTestimonials, createTestimonial, updateTestimonial, deleteTestimonial,
  fetchThemes, createTheme, updateTheme, deleteTheme,
  fetchPress, createPress, updatePress, deletePress,
} from "../lib/adminApi";

const inputCls =
  "w-full bg-[#0b0b0d] border border-white/10 text-white rounded-sm px-4 py-3 focus:outline-none focus:border-[#D97736] focus:ring-1 focus:ring-[#D97736] transition-colors placeholder:text-[#52525b]";
const labelCls = "block text-sm font-medium text-[#F8F8F8] mb-2";
const btnPrimary = "inline-flex items-center justify-center gap-2 bg-[#D97736] text-white px-6 py-3 rounded-sm font-semibold hover:bg-[#B85B22] transition-all disabled:opacity-60";
const btnGhost = "inline-flex items-center gap-2 border border-white/15 text-white px-4 py-2 rounded-sm text-sm hover:border-[#D97736] hover:text-[#D97736] transition-colors";

// ---------- Settings ----------
export function SettingsPanel() {
  const qc = useQueryClient();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { fetchSettings().then(setForm); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await saveSettings(form);
      qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success("Impostazioni salvate");
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="text-[#A1A1AA] flex items-center gap-2"><CircleNotch size={20} className="animate-spin" /> Caricamento...</div>;

  return (
    <form onSubmit={submit} data-testid="settings-panel" className="max-w-xl space-y-5">
      <p className="text-[#A1A1AA] text-sm">Questi dati compaiono nel footer, nella pagina Contatti e nel pulsante WhatsApp del sito.</p>
      <div><label className={labelCls}>Email</label><input data-testid="settings-email" value={form.email} onChange={set("email")} className={inputCls} /></div>
      <div><label className={labelCls}>Telefono</label><input data-testid="settings-phone" value={form.phone} onChange={set("phone")} className={inputCls} /></div>
      <div><label className={labelCls}>WhatsApp (solo numeri, es. 393664307519)</label><input data-testid="settings-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} className={inputCls} /></div>
      <div><label className={labelCls}>Tagline / slogan</label><input data-testid="settings-tagline" value={form.tagline} onChange={set("tagline")} className={inputCls} /></div>
      <div className="pt-4 border-t border-white/10">
        <p className="text-sm font-semibold text-[#D97736] uppercase tracking-wider mb-1">Social & Media</p>
        <p className="text-[#A1A1AA] text-xs mb-4">Incolla i link completi (es. https://instagram.com/tuoprofilo). Le icone appaiono nel footer solo se compilate.</p>
      </div>
      <div><label className={labelCls}>Instagram (URL)</label><input data-testid="settings-instagram" value={form.instagram || ""} onChange={set("instagram")} className={inputCls} placeholder="https://instagram.com/..." /></div>
      <div><label className={labelCls}>TikTok (URL)</label><input data-testid="settings-tiktok" value={form.tiktok || ""} onChange={set("tiktok")} className={inputCls} placeholder="https://tiktok.com/@..." /></div>
      <div><label className={labelCls}>YouTube (URL)</label><input data-testid="settings-youtube" value={form.youtube || ""} onChange={set("youtube")} className={inputCls} placeholder="https://youtube.com/@..." /></div>
      <div><label className={labelCls}>Substack (URL)</label><input data-testid="settings-substack" value={form.substack || ""} onChange={set("substack")} className={inputCls} placeholder="https://tuonome.substack.com" /></div>
      <div><label className={labelCls}>Intervista YouTube (URL) — mostrata in "Chi sono"</label><input data-testid="settings-interview" value={form.interview_url || ""} onChange={set("interview_url")} className={inputCls} placeholder="https://youtube.com/watch?v=..." /></div>
      <button data-testid="settings-save" disabled={saving} className={btnPrimary}>
        {saving ? <CircleNotch size={18} className="animate-spin" /> : <FloppyDisk size={18} />} Salva impostazioni
      </button>
    </form>
  );
}

// ---------- Testimonials ----------
const emptyT = { name: "", role: "", text: "", order: 0 };
export function TestimonialsPanel() {
  const qc = useQueryClient();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // id or "new"
  const [form, setForm] = useState(emptyT);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = () => fetchTestimonials().then(setItems);
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(emptyT); setEditing("new"); };
  const startEdit = (t) => { setForm({ name: t.name, role: t.role, text: t.text, order: t.order || 0 }); setEditing(t.id); };
  const cancel = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing === "new") await createTestimonial(payload);
      else await updateTestimonial(editing, payload);
      await load();
      qc.invalidateQueries({ queryKey: ["testimonials"] });
      toast.success("Testimonianza salvata");
      setEditing(null);
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa testimonianza?")) return;
    await deleteTestimonial(id);
    await load();
    qc.invalidateQueries({ queryKey: ["testimonials"] });
    toast.success("Testimonianza eliminata");
  };

  return (
    <div data-testid="testimonials-panel" className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-[#A1A1AA] text-sm">Le recensioni mostrate in Home. Trascina l'ordine con il campo "Ordine".</p>
        {!editing && <button data-testid="testimonial-new" onClick={startNew} className={btnGhost}><Plus size={16} /> Nuova</button>}
      </div>

      {editing && (
        <form onSubmit={save} className="bg-[#0b0b0d] border border-white/10 rounded-sm p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-1"><label className={labelCls}>Nome *</label><input data-testid="testimonial-name" required value={form.name} onChange={set("name")} className={inputCls} /></div>
            <div className="sm:col-span-1"><label className={labelCls}>Ruolo</label><input data-testid="testimonial-role" value={form.role} onChange={set("role")} className={inputCls} placeholder="Titolare, Sposi..." /></div>
            <div className="sm:col-span-1"><label className={labelCls}>Ordine</label><input data-testid="testimonial-order" type="number" value={form.order} onChange={set("order")} className={inputCls} /></div>
          </div>
          <div><label className={labelCls}>Testo *</label><textarea data-testid="testimonial-text" required rows={3} value={form.text} onChange={set("text")} className={inputCls} /></div>
          <div className="flex gap-3">
            <button data-testid="testimonial-save" disabled={busy} className={btnPrimary}>{busy ? <CircleNotch size={18} className="animate-spin" /> : <FloppyDisk size={18} />} Salva</button>
            <button type="button" onClick={cancel} className={btnGhost}><X size={16} /> Annulla</button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {items.map((t) => (
          <div key={t.id} className="bg-[#111113] border border-white/5 rounded-sm p-5 flex items-start justify-between gap-4">
            <div>
              <div className="font-semibold">{t.name} <span className="text-[#A1A1AA] text-sm font-normal">· {t.role}</span> <span className="text-xs text-[#71717A]">#{t.order}</span></div>
              <p className="text-[#A1A1AA] text-sm mt-1">"{t.text}"</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button data-testid={`testimonial-edit-${t.id}`} onClick={() => startEdit(t)} className="text-[#A1A1AA] hover:text-[#D97736] p-2"><PencilSimple size={18} /></button>
              <button data-testid={`testimonial-delete-${t.id}`} onClick={() => remove(t.id)} className="text-[#A1A1AA] hover:text-red-400 p-2"><Trash size={18} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-[#A1A1AA] text-sm">Nessuna testimonianza.</p>}
      </div>
    </div>
  );
}

// ---------- Event Themes ----------
const emptyTheme = { title: "", description: "", tags: "", order: 0 };
export function ThemesPanel() {
  const qc = useQueryClient();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTheme);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = () => fetchThemes().then(setItems);
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(emptyTheme); setEditing("new"); };
  const startEdit = (t) => { setForm({ title: t.title, description: t.description, tags: (t.tags || []).join(", "), order: t.order || 0 }); setEditing(t.id); };
  const cancel = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
        order: Number(form.order) || 0,
      };
      if (editing === "new") await createTheme(payload);
      else await updateTheme(editing, payload);
      await load();
      qc.invalidateQueries({ queryKey: ["event-themes"] });
      toast.success("Concept salvato");
      setEditing(null);
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo concept?")) return;
    await deleteTheme(id);
    await load();
    qc.invalidateQueries({ queryKey: ["event-themes"] });
    toast.success("Concept eliminato");
  };

  return (
    <div data-testid="themes-panel" className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-[#A1A1AA] text-sm">I "Concept Open Bar a tema" mostrati nella pagina Eventi. Aggiungi i tuoi format.</p>
        {!editing && <button data-testid="theme-new" onClick={startNew} className={btnGhost}><Plus size={16} /> Nuovo concept</button>}
      </div>

      {editing && (
        <form onSubmit={save} className="bg-[#0b0b0d] border border-white/10 rounded-sm p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2"><label className={labelCls}>Titolo *</label><input data-testid="theme-title" required value={form.title} onChange={set("title")} className={inputCls} placeholder="Es. Gatsby & Anni '20" /></div>
            <div><label className={labelCls}>Ordine</label><input data-testid="theme-order" type="number" value={form.order} onChange={set("order")} className={inputCls} /></div>
          </div>
          <div><label className={labelCls}>Descrizione</label><textarea data-testid="theme-description" rows={3} value={form.description} onChange={set("description")} className={inputCls} /></div>
          <div><label className={labelCls}>Tag (separati da virgola)</label><input data-testid="theme-tags" value={form.tags} onChange={set("tags")} className={inputCls} placeholder="Elegante, Vintage, Matrimoni" /></div>
          <div className="flex gap-3">
            <button data-testid="theme-save" disabled={busy} className={btnPrimary}>{busy ? <CircleNotch size={18} className="animate-spin" /> : <FloppyDisk size={18} />} Salva</button>
            <button type="button" onClick={cancel} className={btnGhost}><X size={16} /> Annulla</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((t) => (
          <div key={t.id} className="bg-[#111113] border border-white/5 rounded-sm p-5 flex items-start justify-between gap-4">
            <div>
              <div className="font-semibold">{t.title} <span className="text-xs text-[#71717A]">#{t.order}</span></div>
              <p className="text-[#A1A1AA] text-sm mt-1">{t.description}</p>
              <div className="flex flex-wrap gap-1.5 mt-2">{(t.tags || []).map((tag) => <span key={tag} className="text-xs bg-[#D97736]/15 text-[#D97736] px-2 py-0.5 rounded-sm">{tag}</span>)}</div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button data-testid={`theme-edit-${t.id}`} onClick={() => startEdit(t)} className="text-[#A1A1AA] hover:text-[#D97736] p-2"><PencilSimple size={18} /></button>
              <button data-testid={`theme-delete-${t.id}`} onClick={() => remove(t.id)} className="text-[#A1A1AA] hover:text-red-400 p-2"><Trash size={18} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-[#A1A1AA] text-sm">Nessun concept.</p>}
      </div>
    </div>
  );
}


// ---------- Press / News ----------
const emptyPress = { title: "", source: "", url: "", date: "", excerpt: "", image: "", order: 0 };
export function PressPanel() {
  const qc = useQueryClient();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPress);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = () => fetchPress().then(setItems);
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(emptyPress); setEditing("new"); };
  const startEdit = (p) => { setForm({ title: p.title, source: p.source, url: p.url, date: p.date, excerpt: p.excerpt, image: p.image || "", order: p.order || 0 }); setEditing(p.id); };
  const cancel = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing === "new") await createPress(payload);
      else await updatePress(editing, payload);
      await load();
      qc.invalidateQueries({ queryKey: ["press"] });
      toast.success("Articolo salvato");
      setEditing(null);
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo articolo?")) return;
    await deletePress(id);
    await load();
    qc.invalidateQueries({ queryKey: ["press"] });
    toast.success("Articolo eliminato");
  };

  return (
    <div data-testid="press-panel" className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-[#A1A1AA] text-sm">Articoli e interviste da riviste e magazine, mostrati nella pagina News.</p>
        {!editing && <button data-testid="press-new" onClick={startNew} className={btnGhost}><Plus size={16} /> Nuovo articolo</button>}
      </div>

      {editing && (
        <form onSubmit={save} className="bg-[#0b0b0d] border border-white/10 rounded-sm p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Titolo *</label><input data-testid="press-title" required value={form.title} onChange={set("title")} className={inputCls} placeholder="Titolo dell'articolo" /></div>
            <div><label className={labelCls}>Rivista / Fonte</label><input data-testid="press-source" value={form.source} onChange={set("source")} className={inputCls} placeholder="Es. Bargiornale" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2"><label className={labelCls}>Link all'articolo (URL)</label><input data-testid="press-url" value={form.url} onChange={set("url")} className={inputCls} placeholder="https://..." /></div>
            <div><label className={labelCls}>Data</label><input data-testid="press-date" value={form.date} onChange={set("date")} className={inputCls} placeholder="Es. Marzo 2026" /></div>
          </div>
          <div><label className={labelCls}>Immagine (URL, opzionale)</label><input data-testid="press-image" value={form.image} onChange={set("image")} className={inputCls} placeholder="https://... (facoltativo)" /></div>
          <div><label className={labelCls}>Estratto / descrizione</label><textarea data-testid="press-excerpt" rows={3} value={form.excerpt} onChange={set("excerpt")} className={inputCls} /></div>
          <div><label className={labelCls}>Ordine</label><input data-testid="press-order" type="number" value={form.order} onChange={set("order")} className={`${inputCls} max-w-[120px]`} /></div>
          <div className="flex gap-3">
            <button data-testid="press-save" disabled={busy} className={btnPrimary}>{busy ? <CircleNotch size={18} className="animate-spin" /> : <FloppyDisk size={18} />} Salva</button>
            <button type="button" onClick={cancel} className={btnGhost}><X size={16} /> Annulla</button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {items.map((p) => (
          <div key={p.id} className="bg-[#111113] border border-white/5 rounded-sm p-5 flex items-start justify-between gap-4">
            <div>
              <div className="text-xs text-[#D97736] font-semibold uppercase tracking-wider">{p.source || "Stampa"} {p.date && <span className="text-[#71717A] normal-case">· {p.date}</span>} <span className="text-[#71717A]">#{p.order}</span></div>
              <div className="font-semibold mt-1">{p.title}</div>
              {p.excerpt && <p className="text-[#A1A1AA] text-sm mt-1">{p.excerpt}</p>}
              {p.url && <a href={p.url} target="_blank" rel="noreferrer" className="text-[#D97736] text-xs mt-1 inline-block break-all">{p.url}</a>}
            </div>
            <div className="flex gap-2 shrink-0">
              <button data-testid={`press-edit-${p.id}`} onClick={() => startEdit(p)} className="text-[#A1A1AA] hover:text-[#D97736] p-2"><PencilSimple size={18} /></button>
              <button data-testid={`press-delete-${p.id}`} onClick={() => remove(p.id)} className="text-[#A1A1AA] hover:text-red-400 p-2"><Trash size={18} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-[#A1A1AA] text-sm">Nessun articolo.</p>}
      </div>
    </div>
  );
}
